import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Star, Filter, ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle, Package, Coffee, Fish, Beef, Gift, Wine, Truck, Car, Mail, Leaf, QrCode, CreditCard, Salad } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from 'recharts';
// BMW Service definitions
const services = [{
  id: 'probefahrt',
  name: 'Probefahrt-Service',
  icon: Car
}, {
  id: 'finanzierung',
  name: 'Finanzierungsberatung',
  icon: CreditCard
}, {
  id: 'werkstatt',
  name: 'BMW Werkstatt',
  icon: Package
}, {
  id: 'fahrzeuguebergabe',
  name: 'Fahrzeugübergabe',
  icon: Gift
}, {
  id: 'kundenlounge',
  name: 'Kundenlounge',
  icon: Coffee
}, {
  id: 'zulassung',
  name: 'Zulassungsservice',
  icon: Mail
}, {
  id: 'versicherung',
  name: 'Versicherungsberatung',
  icon: Beef
}, {
  id: 'elektro_beratung',
  name: 'Elektro-/iX-Beratung',
  icon: Leaf
}, {
  id: 'connected_drive',
  name: 'ConnectedDrive Setup',
  icon: QrCode
}, {
  id: 'lieferservice',
  name: 'Fahrzeuglieferung',
  icon: Truck
}, {
  id: 'parkplaetze',
  name: 'Kundenparkplätze',
  icon: Car
}, {
  id: 'online_konfigurator',
  name: 'Online-Konfigurator',
  icon: Salad
}, {
  id: 'loyalty',
  name: 'BMW Loyalty Programm',
  icon: Wine
}, {
  id: 'gebrauchtfahrzeuge',
  name: 'Gebrauchtfahrzeuge',
  icon: Fish
}];
// Autohaus data with services
const marketsData = [{
  id: 'hamburg-niederlassung',
  name: 'BMW Niederlassung Hamburg',
  region: 'Deutschland',
  rating: 4.3,
  size: 2800,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'zulassung', 'versicherung', 'elektro_beratung', 'connected_drive', 'lieferservice', 'parkplaetze', 'online_konfigurator', 'loyalty']
}, {
  id: 'berlin-welzel',
  name: 'Autohaus Welzel Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  size: 1200,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'parkplaetze']
}, {
  id: 'muenchen-niederlassung',
  name: 'BMW Niederlassung München',
  region: 'Deutschland',
  rating: 4.6,
  size: 1800,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'zulassung', 'versicherung', 'elektro_beratung', 'connected_drive', 'lieferservice', 'parkplaetze', 'loyalty']
}, {
  id: 'manhattan-bmw',
  name: 'BMW of Manhattan, New York',
  region: 'USA',
  rating: 3.5,
  size: 1100,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe']
}, {
  id: 'beverly-hills-bmw',
  name: 'BMW of Beverly Hills',
  region: 'USA',
  rating: 4.4,
  size: 1600,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'zulassung', 'versicherung', 'parkplaetze', 'online_konfigurator', 'loyalty']
}, {
  id: 'beijing-bmw',
  name: 'BMW Center Beijing',
  region: 'China',
  rating: 3.9,
  size: 1400,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'parkplaetze', 'gebrauchtfahrzeuge']
}, {
  id: 'shenyang-bmw',
  name: 'BMW Brilliance Shenyang',
  region: 'China',
  rating: 4.5,
  size: 2200,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'zulassung', 'versicherung', 'elektro_beratung', 'lieferservice', 'parkplaetze', 'online_konfigurator', 'loyalty']
}, {
  id: 'london-park-lane',
  name: 'BMW Park Lane, London',
  region: 'Großbritannien',
  rating: 4.2,
  size: 1500,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'versicherung', 'parkplaetze', 'elektro_beratung']
}, {
  id: 'paris-champs',
  name: 'BMW Paris Champs-Élysées',
  region: 'Frankreich',
  rating: 4.1,
  size: 1350,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'parkplaetze', 'elektro_beratung', 'gebrauchtfahrzeuge']
}, {
  id: 'sydney-bmw',
  name: 'BMW Sydney Rushcutters Bay',
  region: 'Australien',
  rating: 4.4,
  size: 1650,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'zulassung', 'versicherung', 'lieferservice', 'parkplaetze', 'loyalty']
}, {
  id: 'tokyo-bmw',
  name: 'BMW Tokyo Aoyama',
  region: 'Japan',
  rating: 4.0,
  size: 1300,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'parkplaetze', 'zulassung']
}, {
  id: 'nuernberg-niederlassung',
  name: 'BMW Niederlassung Nürnberg',
  region: 'Deutschland',
  rating: 4.3,
  size: 1700,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'versicherung', 'parkplaetze', 'connected_drive', 'loyalty']
}, {
  id: 'dresden-niederlassung',
  name: 'BMW Niederlassung Dresden',
  region: 'Deutschland',
  rating: 4.1,
  size: 1250,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'parkplaetze', 'gebrauchtfahrzeuge']
}, {
  id: 'melbourne-bmw',
  name: 'BMW Melbourne Collins Street',
  region: 'Australien',
  rating: 4.2,
  size: 1550,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'fahrzeuguebergabe', 'kundenlounge', 'versicherung', 'parkplaetze', 'elektro_beratung', 'loyalty']
}, {
  id: 'osaka-bmw',
  name: 'BMW Osaka Midosuji',
  region: 'Japan',
  rating: 3.9,
  size: 1150,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'parkplaetze', 'gebrauchtfahrzeuge']
}, {
  id: 'shanghai-bmw',
  name: "BMW Shanghai Jing'an",
  region: 'China',
  rating: 3.6,
  size: 800,
  services: ['werkstatt', 'parkplaetze']
}, {
  id: 'guangzhou-bmw',
  name: 'BMW Guangzhou Tianhe',
  region: 'China',
  rating: 3.8,
  size: 950,
  services: ['probefahrt', 'werkstatt', 'parkplaetze']
}, {
  id: 'berlin-mitte',
  name: 'BMW Niederlassung Berlin-Mitte',
  region: 'Deutschland',
  rating: 3.7,
  size: 1400,
  services: ['probefahrt', 'finanzierung', 'werkstatt', 'connected_drive']
}, {
  id: 'chicago-bmw',
  name: 'BMW of Chicago',
  region: 'USA',
  rating: 3.5,
  size: 900,
  services: ['probefahrt', 'werkstatt']
}, {
  id: 'erfurt-niederlassung',
  name: 'BMW Niederlassung Erfurt',
  region: 'Deutschland',
  rating: 3.8,
  size: 950,
  services: ['probefahrt', 'werkstatt', 'fahrzeuguebergabe', 'parkplaetze']
}, {
  id: 'manchester-bmw',
  name: 'BMW Manchester Deansgate',
  region: 'Großbritannien',
  rating: 3.6,
  size: 880,
  services: ['probefahrt', 'werkstatt', 'parkplaetze', 'gebrauchtfahrzeuge']
}];
// Level options - REMOVED (replaced by scope filter)
const countries = [{
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
export function ServicesReputationPage() {
  const [selectedScope, setSelectedScope] = useState<string>('konzern');
  const [selectedService, setSelectedService] = useState<string | null>(null);
  // Filter markets based on selected scope
  const filteredMarkets = selectedScope === 'konzern' ? marketsData : marketsData.filter(m => {
    const countryName = countries.find(c => c.id === selectedScope)?.name;
    return m.region === countryName;
  });
  // Calculate service impact dynamically based on filtered markets
  const serviceImpactData = services.map(service => {
    const marketsWithService = filteredMarkets.filter(m => m.services.includes(service.id));
    const marketsWithoutService = filteredMarkets.filter(m => !m.services.includes(service.id));
    const avgWithService = marketsWithService.length > 0 ? marketsWithService.reduce((acc, m) => acc + m.rating, 0) / marketsWithService.length : 0;
    const avgWithoutService = marketsWithoutService.length > 0 ? marketsWithoutService.reduce((acc, m) => acc + m.rating, 0) / marketsWithoutService.length : 0;
    return {
      ...service,
      avgRatingWith: avgWithService,
      avgRatingWithout: avgWithoutService,
      impact: avgWithService - avgWithoutService,
      marketsCount: marketsWithService.length,
      penetration: filteredMarkets.length > 0 ? marketsWithService.length / filteredMarkets.length * 100 : 0
    };
  }).sort((a, b) => b.impact - a.impact);
  // Calculate overall stats
  const avgRating = filteredMarkets.length > 0 ? filteredMarkets.reduce((acc, m) => acc + m.rating, 0) / filteredMarkets.length : 0;
  const avgServices = filteredMarkets.length > 0 ? filteredMarkets.reduce((acc, m) => acc + m.services.length, 0) / filteredMarkets.length : 0;
  // Markets sorted by service count
  const marketsByServices = [...filteredMarkets].sort((a, b) => b.services.length - a.services.length);
  const topServiceMarkets = marketsByServices.slice(0, 3);
  const lowServiceMarkets = marketsByServices.slice(-3).reverse();
  // Correlation between service count and rating
  const correlationData = filteredMarkets.map(m => ({
    name: m.name,
    serviceCount: m.services.length,
    rating: m.rating,
    z: m.size
  }));
  // Calculate correlation
  const n = correlationData.length;
  const sumX = correlationData.reduce((acc, d) => acc + d.serviceCount, 0);
  const sumY = correlationData.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = correlationData.reduce((acc, d) => acc + d.serviceCount * d.rating, 0);
  const sumX2 = correlationData.reduce((acc, d) => acc + d.serviceCount * d.serviceCount, 0);
  const sumY2 = correlationData.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const denom = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  const correlation = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  // Top impact services
  const topImpactServices = serviceImpactData.filter(s => s.impact > 0).slice(0, 5);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-1258">
      {/* Scope Filter */}
      <Card className="!p-4" data-id="element-1259">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-1260">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-1261">
            <Filter size={16} data-id="element-1262" />
            <span data-id="element-1263">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-1264">
            <button onClick={() => setSelectedScope('konzern')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${selectedScope === 'konzern' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedScope === 'konzern' ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1265">
              <Building2 size={14} data-id="element-1266" />
              Konzern-Übersicht
            </button>

            <div className="flex items-center" data-id="element-1267">
              <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-1268" />
            </div>

            {countries.map(country => <button key={country.id} onClick={() => setSelectedScope(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedScope === country.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedScope === country.id ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-1269">
                {country.name}
              </button>)}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1270">
        <KPICard title="Korrelation Services ↔ Rating" value={isNaN(correlation) ? '–' : correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-1272" />} subtext="Pearson-Koeffizient" data-id="element-1271" />
        <KPICard title="Ø Services pro Autohaus" value={avgServices.toFixed(1)} change={`von ${services.length} möglich`} trend="up" icon={<Package size={18} data-id="element-1274" />} subtext="Durchschnitt" data-id="element-1273" />
        <KPICard title="Top Service-Effekt" value={`+${topImpactServices[0]?.impact.toFixed(2) || 0}★`} change={topImpactServices[0]?.name || '-'} trend="up" icon={<Star size={18} data-id="element-1276" />} subtext="Rating-Unterschied" data-id="element-1275" />
        <KPICard title="Ø Bewertung" value={avgRating.toFixed(2)} change="+0.15" trend="up" icon={<Star size={18} data-id="element-1278" />} subtext="Google Rating" data-id="element-1277" />
      </div>

      {/* Service Count vs Rating Scatter */}
      <Card data-id="element-1279">
        <div className="p-6" data-id="element-1280">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1281">
            Anzahl Services vs. Rating
          </h3>
          <p className="text-sm text-gray-500 mb-4" data-id="element-1282">
            Korrelation zwischen Service-Angebot und Bewertung
          </p>
          <div className="h-72" data-id="element-1283">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1284">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-1285">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1286" />
                <XAxis type="number" dataKey="serviceCount" name="Services" domain={[0, 15]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Anzahl Services',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1287" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Rating',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-1288" />
                <ZAxis type="number" dataKey="z" range={[100, 500]} data-id="element-1289" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} content={({
                active,
                payload
              }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100" data-id="element-1291">
                          <p className="font-medium text-gray-900 text-sm mb-2" data-id="element-1292">
                            {data.name.replace('BMW Niederlassung ', '').replace('Autohaus ', '')}
                          </p>
                          <div className="space-y-1 text-xs" data-id="element-1293">
                            <p className="text-gray-600" data-id="element-1294">
                              <span className="font-medium" data-id="element-1295">Services:</span>{' '}
                              {data.serviceCount}
                            </p>
                            <p className="text-gray-600" data-id="element-1296">
                              <span className="font-medium" data-id="element-1297">Rating:</span>{' '}
                              {data.rating.toFixed(1)} ★
                            </p>
                          </div>
                        </div>;
                }
                return null;
              }} data-id="element-1290" />
                <Scatter name="Autohäuser" data={correlationData} fill="#1C69D4" data-id="element-1298">
                  {correlationData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.serviceCount >= avgServices ? '#639754' : '#E03C32'} fillOpacity={0.7} data-id="element-1299" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-1300">
            <div className="text-sm text-blue-800" data-id="element-1301">
              <strong data-id="element-1302">
                Korrelation:{' '}
                {isNaN(correlation) ? '–' : (correlation * 100).toFixed(0)}%
              </strong>{' '}
              – Autohäuser mit mehr Services haben tendenziell bessere
              Bewertungen.
            </div>
          </div>
        </div>
      </Card>

      {/* Service Impact Chart */}
      <Card data-id="element-1303">
        <div className="p-6" data-id="element-1304">
          <div className="flex items-center justify-between mb-6" data-id="element-1305">
            <div data-id="element-1306">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-1307">
                Service-Auswirkung auf Rating
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1308">
                Durchschnittliches Rating: Mit Service vs. Ohne Service
              </p>
            </div>
          </div>
          <div className="h-[500px]" data-id="element-1309">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1310">
              <BarChart data={serviceImpactData} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 160,
              bottom: 5
            }} data-id="element-1311">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} data-id="element-1312" />
                <XAxis type="number" domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-1313" />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#374151'
              }} width={155} data-id="element-1314" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [value.toFixed(2) + ' ★', name === 'avgRatingWith' ? 'Mit Service' : 'Ohne Service']} data-id="element-1315" />
                <Bar dataKey="avgRatingWith" name="Mit Service" fill="#639754" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1316" />
                <Bar dataKey="avgRatingWithout" name="Ohne Service" fill="#E03C32" radius={[0, 4, 4, 0]} barSize={16} data-id="element-1317" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-8 text-sm" data-id="element-1318">
            <div className="flex items-center gap-2" data-id="element-1319">
              <div className="w-3 h-3 rounded" style={{
              backgroundColor: '#639754'
            }} data-id="element-1320"></div>
              <span className="text-gray-600" data-id="element-1321">Mit Service</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1322">
              <div className="w-3 h-3 rounded" style={{
              backgroundColor: '#E03C32'
            }} data-id="element-1323"></div>
              <span className="text-gray-600" data-id="element-1324">Ohne Service</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Top Impact Services */}
      <Card data-id="element-1325">
        <div className="p-6" data-id="element-1326">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-1327">
            <ArrowUpRight className="text-green-600" size={20} data-id="element-1328" />
            Höchster Rating-Effekt
          </h3>
          <p className="text-sm text-gray-500 mb-4" data-id="element-1329">
            BMW Services mit größtem positiven Einfluss auf Bewertungen
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-1330">
            {topImpactServices.map((service, index) => {
            const ServiceIcon = service.icon;
            return <motion.div key={service.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex flex-col items-center p-4 rounded-lg text-center" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-1331">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-3" style={{
                backgroundColor: '#639754'
              }} data-id="element-1332">
                    <ServiceIcon size={24} className="text-white" data-id="element-1333" />
                  </div>
                  <div className="font-medium text-gray-900 text-sm mb-1" data-id="element-1334">
                    {service.name}
                  </div>
                  <div className="font-bold text-xl" style={{
                color: '#639754'
              }} data-id="element-1335">
                    +{service.impact.toFixed(2)} ★
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-1336">
                    {service.marketsCount} Häuser (
                    {service.penetration.toFixed(0)}%)
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </Card>

      {/* Service Penetration Table */}
      <Card data-id="element-1337">
        <div className="p-6" data-id="element-1338">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1339">
            Service-Übersicht
          </h3>
          <div className="overflow-x-auto" data-id="element-1340">
            <table className="w-full text-sm" data-id="element-1341">
              <thead data-id="element-1342">
                <tr className="border-b border-gray-200" data-id="element-1343">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-1344">
                    Service
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1345">
                    Autohäuser
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1346">
                    Penetration
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1347">
                    Ø Rating mit
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1348">
                    Ø Rating ohne
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-1349">
                    Effekt
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1350">
                {serviceImpactData.map(service => {
                const ServiceIcon = service.icon;
                return <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1351">
                      <td className="py-3 px-4" data-id="element-1352">
                        <div className="flex items-center gap-2" data-id="element-1353">
                          <ServiceIcon size={16} className="text-gray-400" data-id="element-1354" />
                          <span className="font-medium text-gray-900" data-id="element-1355">
                            {service.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-1356">
                        {service.marketsCount}
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1357">
                        <div className="flex items-center justify-end gap-2" data-id="element-1358">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1359">
                            <div className="h-full rounded-full" style={{
                          width: `${service.penetration}%`,
                          backgroundColor: '#1C69D4'
                        }} data-id="element-1360" />
                          </div>
                          <span className="text-gray-500 w-10 text-right" data-id="element-1361">
                            {service.penetration.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-gray-900" data-id="element-1362">
                        {service.avgRatingWith.toFixed(2)} ★
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-1363">
                        {service.avgRatingWithout.toFixed(2)} ★
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-1364">
                        <span className={`font-medium ${service.impact > 0 ? 'text-green-600' : service.impact < 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1365">
                          {service.impact > 0 ? '+' : ''}
                          {service.impact.toFixed(2)} ★
                        </span>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}